import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import QuestionChip from "components/Styled/QuestionChip";
import StyledSelect from "components/Styled/Select";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";

interface Props {
  open: boolean;
  handleClose: () => void;
  template: Template;
  handleConfirm: (replacedTemplateId: number | null) => void;
  loading: boolean;
}

const DeleteTemplateDialog = ({
  open,
  handleClose,
  template,
  handleConfirm,
  loading,
}: Props) => {
  const { outreachTemplates, followupTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const [updatePendingEmails, setUpdatePendingEmails] = useState(true);
  const [replacedTemplateId, setReplacedTemplateId] = useState<number | null>(
    null,
  );

  const getReplacedTemplates = () => {
    if (template?.contentType === TemplateContentType.OUTREACH) {
      return outreachTemplates?.filter((x) => x.id !== template.id);
    } else {
      return followupTemplates?.filter((x) => x.id !== template.id);
    }
  };

  const replacedTemplates = getReplacedTemplates();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h4">Delete Template</Typography>
      </DialogTitle>

      <DialogContent>
        {template?.attachedScheduledEmailsCount &&
        replacedTemplates?.length > 0 ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              border: `1px solid ${grey[300]}`,
              mb: 2,
            }}
          >
            <Typography gutterBottom>
              This template is currently used in{" "}
              {template?.attachedScheduledEmailsCount ? (
                <>
                  {" "}
                  <strong>
                    {template?.attachedScheduledEmailsCount} scheduled emails
                  </strong>
                </>
              ) : (
                ""
              )}
              . Do you want to replace the email content with a different
              template?
            </Typography>

            <Typography variant="caption" paragraph>
              Pick an option below
            </Typography>

            <Grid container gap={1} alignItems={"center"}>
              <QuestionChip
                isSelected={!updatePendingEmails}
                onClick={() => setUpdatePendingEmails(false)}
                label="No"
              />
              <QuestionChip
                isSelected={updatePendingEmails}
                onClick={() => setUpdatePendingEmails(true)}
                label="Yes, replace with:"
              />

              <StyledSelect
                variant="standard"
                styles={{ width: 200 }}
                value={replacedTemplateId || ""}
                onChange={(e: CustomEvent) => {
                  setUpdatePendingEmails(true);
                  setReplacedTemplateId(e.target.value);
                }}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {getReplacedTemplates()?.map((replacedTemplate) => (
                  <MenuItem
                    key={replacedTemplate?.id}
                    value={replacedTemplate?.id}
                  >
                    {replacedTemplate?.name}
                  </MenuItem>
                ))}
              </StyledSelect>
            </Grid>
          </Box>
        ) : (
          <Typography>
            Are you sure you want to delete {template?.name}?
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button sx={{ color: "purple.main" }} onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          disableElevation
          sx={{ px: 2 }}
          onClick={() => handleConfirm(replacedTemplateId)}
        >
          Confirm
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTemplateDialog;
